/**
 * Question Card Component
 * Displays a single question in the Question Bank
 */

import React from 'react';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { 
  FileText, 
  ListChecks, 
  CheckSquare, 
  Hash, 
  Calendar,
  AlignLeft,
  ToggleLeft,
  Pencil,
  ListPlus
} from 'lucide-react';
import type { Question } from '../../utils/api/questionBankApi';
import { cn } from '../../lib/utils';

interface QuestionCardProps {
  question: Question;
  viewMode: 'grid' | 'list' | 'table';
  isSelected: boolean;
  onSelect: () => void;
  onClick: () => void;
}

// Question type icons
const questionTypeIcons = {
  text: FileText,
  textarea: AlignLeft,
  select: ListChecks,
  multiselect: CheckSquare,
  boolean: ToggleLeft,
  number: Hash,
  date: Calendar,
  list_builder: ListPlus,
};

// Question type labels
const questionTypeLabels = {
  text: 'Text',
  textarea: 'Textarea',
  select: 'Select',
  multiselect: 'Multi-select',
  boolean: 'Yes/No',
  number: 'Number',
  date: 'Date',
  list_builder: 'List Builder',
};

// Phase colors
const phaseColors = {
  discovery: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  scoping: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  implementation: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
};

export function QuestionCard({
  question,
  viewMode,
  isSelected,
  onSelect,
  onClick,
}: QuestionCardProps) {
  const Icon = questionTypeIcons[question.type];

  if (viewMode === 'grid') {
    return (
      <div
        className={cn(
          'relative border rounded-lg p-4 hover:shadow-md transition-all group',
          'bg-card hover:border-primary',
          isSelected && 'border-primary ring-2 ring-primary/20'
        )}
      >
        {/* Selection Checkbox */}
        <div 
          className="checkbox-wrapper absolute top-2 left-2" 
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
          />
        </div>
        
        {/* Edit Button - Shows on hover */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>

        {/* Type Icon */}
        <div className="flex items-start gap-3 ml-6">
          <div className="p-2 rounded-md bg-primary/10">
            <Icon className="w-4 h-4 text-primary" />
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Question ID */}
            <div className="flex items-center gap-2 mb-1">
              <code className="text-xs font-mono text-muted-foreground">
                {question.id}
              </code>
              {question.required && (
                <Badge variant="destructive" className="text-xs">Required</Badge>
              )}
              {question.critical && (
                <Badge variant="default" className="text-xs">Critical</Badge>
              )}
            </div>

            {/* Question Text */}
            <h3 className="font-semibold text-base mb-2 line-clamp-2">
              {question.text}
            </h3>

            {/* Category & Phase */}
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-xs">
                {question.category}
              </Badge>
              <Badge className={cn('text-xs', phaseColors[question.phase])}>
                {question.phase}
              </Badge>
              {question.grouping.isPartOfWizard && (
                <Badge variant="secondary" className="text-xs">
                  Wizard
                </Badge>
              )}
            </div>

            {/* Tags */}
            {question.tags && question.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {question.tags.slice(0, 3).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {question.tags.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{question.tags.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {/* Usage Count */}
            {question.usageCount !== undefined && question.usageCount > 0 && (
              <div className="text-xs text-muted-foreground mt-2">
                Used in {question.usageCount} config items
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div
        className={cn(
          'flex items-center gap-4 p-3 border rounded-lg hover:bg-accent transition-colors group',
          isSelected && 'border-primary bg-accent'
        )}
      >
        {/* Selection Checkbox */}
        <div 
          className="checkbox-wrapper" 
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox checked={isSelected} onCheckedChange={onSelect} />
        </div>

        {/* Type Icon */}
        <div className="p-2 rounded-md bg-primary/10 flex-shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </div>

        {/* Question Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-xs font-mono text-muted-foreground">
              {question.id}
            </code>
            <span className="text-sm font-medium truncate">{question.text}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{question.category}</Badge>
            <Badge className={cn('text-xs', phaseColors[question.phase])}>
              {question.phase}
            </Badge>
            {question.required && (
              <Badge variant="destructive" className="text-xs">Required</Badge>
            )}
            {question.grouping.isPartOfWizard && (
              <Badge variant="secondary" className="text-xs">Wizard</Badge>
            )}
          </div>
        </div>

        {/* Type Label */}
        <Badge variant="outline" className="text-xs flex-shrink-0">
          {questionTypeLabels[question.type]}
        </Badge>
        
        {/* Edit Button - Always visible */}
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="flex-shrink-0"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit
        </Button>
      </div>
    );
  }

  // Table view (simplified for now)
  return (
    <tr
      className={cn(
        'hover:bg-accent cursor-pointer transition-colors',
        isSelected && 'bg-accent'
      )}
      onClick={onClick}
    >
      <td className="p-2">
        <Checkbox 
          checked={isSelected} 
          onCheckedChange={onSelect}
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="p-2">
        <code className="text-xs font-mono">{question.id}</code>
      </td>
      <td className="p-2 max-w-md truncate">{question.text}</td>
      <td className="p-2">
        <Badge variant="outline" className="text-xs">{question.category}</Badge>
      </td>
      <td className="p-2">{questionTypeLabels[question.type]}</td>
      <td className="p-2">
        <Badge className={cn('text-xs', phaseColors[question.phase])}>
          {question.phase}
        </Badge>
      </td>
      <td className="p-2 text-center">
        {question.required && '✓'}
      </td>
    </tr>
  );
}